<template>
  <div class="site-content-editor" v-loading="loading">
    <el-alert title="这里保存的内容会直接同步到官网对应栏目。" type="info" :closable="false" show-icon />
    <el-form ref="contentForm" :model="formData" label-position="top" class="content-form">
      <template v-for="field in page.fields">
        <h3 v-if="field.type === 'section'" :key="`section-${field.label}`" class="form-section-title">{{ field.label }}</h3>

        <el-form-item v-else-if="field.type === 'image'" :key="field.key" :label="field.label">
          <el-upload
            class="content-image-uploader"
            action="/api/UpLoad/UploadImage"
            :headers="headers"
            :show-file-list="false"
            :on-success="response => imageUploaded(field.key, response)"
          >
            <img v-if="formData[field.key]" :src="formData[field.key]" class="content-image-preview" />
            <div v-else class="content-image-placeholder"><i class="el-icon-plus"></i><span>上传图片</span></div>
          </el-upload>
          <el-input v-model="formData[field.key]" class="image-path-input" placeholder="也可直接填写图片地址" />
        </el-form-item>

        <div v-else-if="field.type === 'list'" :key="`list-${field.label}`" class="list-editor">
          <div class="list-heading">
            <div><strong>{{ field.label }}</strong><small>可新增、删除并调整显示顺序</small></div>
            <el-button size="small" type="primary" icon="el-icon-plus" @click="addListItem(field)">新增一项</el-button>
          </div>
          <div v-if="!formData[field.key] || !formData[field.key].length" class="list-empty">暂无内容，请点击“新增一项”</div>
          <article v-for="(item, index) in formData[field.key]" :key="`${field.key}-${index}`" class="list-card">
            <header>
              <span>{{ field.label }} {{ index + 1 }}</span>
              <div>
                <el-button size="mini" icon="el-icon-top" :disabled="index === 0" aria-label="上移" @click="moveListItem(field.key, index, -1)" />
                <el-button size="mini" icon="el-icon-bottom" :disabled="index === formData[field.key].length - 1" aria-label="下移" @click="moveListItem(field.key, index, 1)" />
                <el-button size="mini" type="danger" icon="el-icon-delete" aria-label="删除" @click="removeListItem(field.key, index)" />
              </div>
            </header>
            <el-row :gutter="16">
              <el-col v-for="itemField in field.itemFields" :key="itemField.key" :xs="24" :sm="itemField.type === 'textarea' ? 24 : 12">
                <el-form-item :label="itemField.label">
                  <el-input v-model="item[itemField.key]" :type="itemField.type || 'text'" :rows="itemField.rows || 3" />
                  <small v-if="itemField.help" class="field-help">{{ itemField.help }}</small>
                </el-form-item>
              </el-col>
            </el-row>
          </article>
        </div>

        <el-form-item v-else :key="field.key" :label="field.label">
          <el-input v-model="formData[field.key]" :type="field.type || 'text'" :rows="field.rows || 3" :placeholder="field.placeholder || `请输入${field.label}`" />
          <small v-if="field.help" class="field-help">{{ field.help }}</small>
        </el-form-item>
      </template>

      <div class="save-bar">
        <el-button type="primary" :loading="saving" @click="save">保存并同步官网</el-button>
        <el-button @click="resetDefaults">恢复当前默认内容</el-button>
      </div>
    </el-form>
  </div>
</template>

<script>
import { getSiteContentPage, getSiteContentDefaults } from "../../site-content-config";

export default {
  name: "SiteContent",
  data() {
    return { loading: true, saving: false, recordId: 0, formData: {} };
  },
  computed: {
    pageName() { return this.$route.meta.contentKey || "home"; },
    page() { return getSiteContentPage(this.pageName); },
    headers() { return { Authorization: "Browser " + sessionStorage.getItem("token") }; },
    options() { return { headers: this.headers }; }
  },
  watch: { "$route.path": "loadData" },
  mounted() { this.loadData(); },
  methods: {
    normalizeContent(value) {
      const defaults = getSiteContentDefaults(this.pageName);
      const merged = { ...defaults, ...value };
      this.page.fields.filter(field => field.type === "list").forEach(field => {
        if (!Array.isArray(merged[field.key])) merged[field.key] = defaults[field.key] || [];
      });
      return merged;
    },
    loadData() {
      this.loading = true;
      this.recordId = 0;
      this.formData = this.normalizeContent({});
      this.$http.get(`DataDictionary/GetDataDictionaryAll?key=${encodeURIComponent(this.page.key)}`)
        .then(response => {
          const record = Array.isArray(response.data) ? response.data[0] : null;
          if (!record) return;
          this.recordId = record.Id;
          try { this.formData = this.normalizeContent(JSON.parse(record.Content || "{}")); }
          catch (error) { this.$message.warning("已使用默认内容，原配置格式无法读取"); }
        })
        .catch(error => this.$message.error("读取内容失败：" + error))
        .finally(() => { this.loading = false; });
    },
    resetDefaults() { this.formData = this.normalizeContent({}); },
    imageUploaded(key, response) { this.$set(this.formData, key, response); },
    addListItem(field) {
      if (!Array.isArray(this.formData[field.key])) this.$set(this.formData, field.key, []);
      this.formData[field.key].push(JSON.parse(JSON.stringify(field.defaultItem)));
    },
    moveListItem(key, index, direction) {
      const target = index + direction;
      if (target < 0 || target >= this.formData[key].length) return;
      const list = this.formData[key].slice();
      const item = list.splice(index, 1)[0];
      list.splice(target, 0, item);
      this.$set(this.formData, key, list);
    },
    removeListItem(key, index) { this.formData[key].splice(index, 1); },
    save() {
      this.saving = true;
      const payload = { Id: this.recordId, Key: this.page.key, Content: JSON.stringify(this.formData), CreateTime: new Date() };
      const action = this.recordId ? "ModifiedDataDictionary" : "CreateDataDictionary";
      this.$http.post(`DataDictionary/${action}`, payload, this.options)
        .then(response => {
          if (!this.recordId && response.data) this.recordId = response.data.Id;
          this.$message.success("已保存并同步到官网");
        })
        .catch(error => this.$message.error("保存失败：" + error))
        .finally(() => { this.saving = false; });
    }
  }
};
</script>

<style scoped>
.site-content-editor { max-width: 960px; }
.content-form { margin-top: 28px; }
.form-section-title { margin: 34px 0 20px; padding: 12px 16px; border-left: 4px solid #3157c8; color: #1b2a44; background: #f4f7fc; }
.field-help { display: block; margin-top: 6px; color: #8793a8; line-height: 1.5; }
.content-image-uploader { display: inline-block; }
.content-image-preview, .content-image-placeholder { width: 220px; min-height: 110px; border: 1px dashed #c7d0df; border-radius: 8px; object-fit: contain; background: #f8faff; }
.content-image-placeholder { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; color: #7a879c; cursor: pointer; }
.image-path-input { max-width: 520px; margin-left: 16px; vertical-align: top; }
.list-editor { margin: 28px 0; }
.list-heading { margin-bottom: 14px; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.list-heading strong { display: block; font-size: 16px; }.list-heading small { display: block; margin-top: 4px; color: #8793a8; }
.list-card { margin-bottom: 14px; padding: 18px 20px 2px; border: 1px solid #e1e7f1; border-radius: 10px; background: #fbfcff; }
.list-card > header { margin-bottom: 14px; display: flex; justify-content: space-between; align-items: center; font-weight: 700; }
.list-empty { padding: 28px; border: 1px dashed #d5dce8; border-radius: 8px; text-align: center; color: #8a96a8; }
.save-bar { position: sticky; bottom: 0; z-index: 5; margin-top: 30px; padding: 16px; border-top: 1px solid #e4e9f2; background: rgba(255,255,255,.96); }
@media (max-width: 600px) { .image-path-input { display: block; margin: 12px 0 0; }.list-heading { align-items: flex-start; }.list-card { padding: 14px 14px 2px; } }
</style>
